import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMemo, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const video = useMemo(() => videos.find((v) => v.id === Number(id)), [id]);
  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return videos
      .filter((v) => v.id !== video.id && v.category === video.category)
      .slice(0, 4);
  }, [video]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">内容未找到</h1>
          <p className="text-foreground-500 mb-6">您访问的影视内容不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === video.category);

  const handleArticleClick = (articleId: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`/article/${articleId}`);
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: video.category === 'tv' ? '电视剧' : video.category === 'movie' ? '电影' : video.category === 'variety' ? '综艺' : video.category === 'anime' ? '动漫' : '纪录片', href: `/category/${video.category}` },
            { label: video.title },
          ]}
        />

        {/* Article Header */}
        <section className="w-full bg-background-50 py-8 md:py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="w-full lg:w-[350px] shrink-0">
                <div className="relative overflow-hidden rounded-2xl aspect-[3/4]">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - 青柠影视高清在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium">
                    <i className="ri-star-fill mr-1"></i>{video.rating} 分
                  </span>
                  <span className="text-sm text-foreground-500">{video.year}</span>
                  <span className="text-sm text-foreground-500">·</span>
                  <span className="text-sm text-foreground-500">{video.region}</span>
                  <span className="text-sm text-foreground-500">·</span>
                  <span className="text-sm text-foreground-500">{video.type}</span>
                  <span className="text-sm text-foreground-500">·</span>
                  <span className="px-2 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {video.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 p-5 bg-background-100 rounded-xl">
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">导演</p>
                    <p className="text-sm font-medium text-foreground-800">{video.director}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">主演</p>
                    <p className="text-sm font-medium text-foreground-800">{video.actors.slice(0, 3).join(' / ')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">集数/时长</p>
                    <p className="text-sm font-medium text-foreground-800">{video.duration}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">更新状态</p>
                    <p className="text-sm font-medium text-foreground-800">{video.updateInfo}</p>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {video.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {video.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Videos */}
        {relatedVideos.length > 0 && (
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((v) => (
                  <article
                    key={v.id}
                    className="group cursor-pointer rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300"
                    onClick={() => handleArticleClick(v.id)}
                  >
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img
                        src={v.image}
                        alt={v.title}
                        title={`${v.title} - 青柠影视高清在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 text-xs rounded-md bg-black/60 text-white">{v.rating}</span>
                      </div>
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <i className="ri-play-fill text-xl text-background-50 ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1">{v.title}</h3>
                      <p className="text-xs text-foreground-400 mt-1">{v.year} · {v.region}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      {/* JSON-LD for Article/Video */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: video.title,
            description: video.description,
            image: video.image,
            dateCreated: `${video.year}`,
            director: { '@type': 'Person', name: video.director },
            actor: video.actors.map((a: string) => ({ '@type': 'Person', name: a })),
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: video.rating,
              bestRating: '10',
            },
            countryOfOrigin: { '@type': 'Country', name: video.region },
            genre: video.tags,
          }),
        }}
      />

      {/* BreadcrumbList JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.qingning.tv/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: categoryInfo?.name || video.category,
                item: `https://www.qingning.tv/category/${video.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: video.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}